import React from "react";
import { Moon, Sun } from "lucide-react";
import { useChipMonkTheme } from "../theme/ThemeProvider";
import { cx } from "./format";

import markSm from "../brand/logos/chipmonk-mark-sm.webp";
import markMd from "../brand/logos/chipmonk-mark-md.webp";
import wordDark from "../brand/logos/chipmonk-wordmark-on-dark-sm.webp";
import wordLight from "../brand/logos/chipmonk-wordmark-on-light-sm.webp";
import fullDark from "../brand/logos/chipmonk-full-on-dark-md.webp";
import fullLight from "../brand/logos/chipmonk-full-on-light-md.webp";
import plDark from "../brand/logos/poker-launcher-on-dark-sm.webp";
import plLight from "../brand/logos/poker-launcher-on-light-sm.webp";

/** The ChipMonk mascot on its own. */
export const BrandMark = ({ size = 32, large = false, className, alt = "" }) => (
  <img
    src={large || size > 96 ? markMd : markSm}
    alt={alt}
    width={size}
    style={{ width: size, height: "auto" }}
    className={cx("select-none shrink-0", className)}
    draggable={false}
  />
);

/** "CHIPMONK" wordmark, in the right colours for the current theme. */
export const Wordmark = ({ height = 18, className }) => {
  const { mode } = useChipMonkTheme();
  return (
    <img
      src={mode === "dark" ? wordDark : wordLight}
      alt="ChipMonk"
      style={{ height, width: "auto" }}
      className={cx("select-none", className)}
      draggable={false}
    />
  );
};

/** Mascot + wordmark stacked. For login, splash and big empty states. */
export const FullLogo = ({ width = 220, className }) => {
  const { mode } = useChipMonkTheme();
  return (
    <img
      src={mode === "dark" ? fullDark : fullLight}
      alt="ChipMonk"
      style={{ width, maxWidth: "100%", height: "auto" }}
      className={cx("select-none", className)}
      draggable={false}
    />
  );
};

/** Small "by Poker Launcher" mark. Footers only; always smaller than ChipMonk. */
export const ByPokerLauncher = ({ height = 22, className }) => {
  const { mode } = useChipMonkTheme();
  return (
    <span className={cx("inline-flex items-center gap-2 text-[11px] text-cm-faint", className)}>
      <span>by</span>
      <img
        src={mode === "dark" ? plDark : plLight}
        alt="Poker Launcher"
        style={{ height, width: "auto" }}
        draggable={false}
      />
    </span>
  );
};

/** Sun/moon button that flips the whole app between dark and light. */
export const ThemeToggle = ({ className, withLabel = false }) => {
  const { mode, toggle } = useChipMonkTheme();
  const next = mode === "dark" ? "light" : "dark";
  const Icon = mode === "dark" ? Sun : Moon;
  return (
    <button
      type="button"
      onClick={toggle}
      className={cx(
        "inline-flex items-center justify-center gap-2 h-9 rounded-cm border border-cm-border bg-cm-surface-2 text-cm-muted hover:text-cm-text hover:border-cm-border-strong transition-colors",
        withLabel ? "px-3 text-sm font-medium" : "w-9",
        className,
      )}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
    >
      <Icon size={16} />
      {withLabel && <span>{next === "light" ? "Light mode" : "Dark mode"}</span>}
    </button>
  );
};
